import { useState, useEffect } from "react";
import { Box, TextField, Grid, Paper, MenuItem } from "@mui/material";
import axios from "../../../config/axios";
import {
  resolveErrMsg,
  getUserData,
  hasAccess,
} from "../../../utils/utilities";
import CommonDialog from "../../../components/CommonDialog";
import FormLabel from "../../../components/FormLabel";

const LinkProfileModal = (props) => {
  const [userData, setUserData] = useState({ isAdmin: false });
  const [btnLoader, setBtnLoader] = useState(false);
  const [validations, setValidations] = useState({});
  const [optionLoading, setOptionLoading] = useState(false);
  const [userOptions, setUserOptions] = useState([]);
  const [roleOptions, setRoleOptions] = useState(["User", "Admin"]);

  // other functions
  const handleCloseModal = () => {
    props.setOpen(false);
  };

  const handleChange = (field, value) => {
    setUserData((prevState) => ({
      ...prevState,
      [`${field}`]: value,
    }));
  };

  const resolveLink = () => {
    hasAccess() ? handleLinkProfileToAdmin() : handleLinkProfileToUser();
  };

  // API Services
  const handleLinkProfileToUser = () => {
    setBtnLoader(true);
    setValidations({});
    const body = {
      email: userData.email || "",
      name: userData.name || "",
      profileId: props.profileId,
      adminId: getUserData().id,
    };
    axios
      .put("/employee-profile/assign-to-user", body)
      .then((res) => {
        if (res.status === 200) {
          if (res.data.status) {
            props.setAlertVariant("success");
            props.setAlertMessage("Profile Linked Successfully!");
            props.setAlert(true);
            handleCloseModal();
            props.getList();
          } else {
            if (res.data.type === "validation") {
              const tempVal = {};
              res.data.error?.forEach((err) => {
                tempVal[err.field] = resolveErrMsg(err.type);
              });
              setValidations(tempVal);
            }
            if (res.data.type === "error") {
              props.setAlertVariant("error");
              props.setAlertMessage(res.data.error?.message);
              props.setAlert(true);
            }
          }
        }
        setBtnLoader(false);
      })
      .catch((err) => console.log(err));
  };

  const handleLinkProfileToAdmin = () => {
    setBtnLoader(true);
    setValidations({});
    const body = {
      email: userData.email || "",
      name: userData.name || "",
      profileId: props.profileId,
      companyId: props?.companyId,
    };
    axios
      .put("/employee-profile/assign-to-admin", body)
      .then((res) => {
        if (res.status === 200) {
          if (res.data.status) {
            props.setAlertVariant("success");
            props.setAlertMessage("Profile Linked Successfully!");
            props.setAlert(true);
            handleCloseModal();
            props.getList();
          } else {
            if (res.data.type === "validation") {
              const tempVal = {};
              res.data.error?.forEach((err) => {
                tempVal[err.field] = resolveErrMsg(err.type);
              });
              setValidations(tempVal);
            }
            if (res.data.type === "error") {
              props.setAlertVariant("error");
              props.setAlertMessage(res.data.error?.message);
              props.setAlert(true);
            }
          }
        }
        setBtnLoader(false);
      })
      .catch((err) => console.log(err));
  };

  const getAllUserOptions = () => {
    setOptionLoading(true);
    axios
      .get("/admin/all-users-list", { params: { companyId: props?.companyId } })
      .then((res) => {
        if (res.status === 200) {
          if (res.data.status) {
            setUserOptions(res.data.data || []);
          }
          setOptionLoading(false);
        }
      })
      .catch((err) => console.log(err));
  };

  const getUserOptions = () => {
    setOptionLoading(true);
    axios
      .get("/user/user-options", { params: { companyId: props?.companyId } })
      .then((res) => {
        if (res.status === 200) {
          if (res.data.status) {
            setUserOptions(res.data.data || []);
          }
          setOptionLoading(false);
        }
      })
      .catch((err) => console.log(err));
  };

  useEffect(() => {
    if (hasAccess()) {
      getAllUserOptions();
    } else {
      getUserOptions();
    }
    return () => {
      props.setProfileId("");
    };
  }, []);

  return (
    <CommonDialog
      open={props.open}
      title={"Link Profile"}
      onSubmit={resolveLink}
      onClose={handleCloseModal}
      loading={btnLoader}
      submitLabel="Link"
    >
      <Paper style={{ ...props.style, width: "100%" }} elevation={0}>
        <Box sx={{ padding: "10px" }}>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <FormLabel title="Name" />
              <TextField
                placeholder="Name"
                size="small"
                error={validations.name}
                helperText={validations.name}
                value={userData.name || ""}
                onChange={(e) => handleChange("name", e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <FormLabel title="Email" />
              <TextField
                placeholder="Email"
                size="small"
                error={validations.email}
                helperText={validations.email}
                value={userData.email || ""}
                onChange={(e) => handleChange("email", e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <FormLabel title="Role" />
              <TextField
                select
                fullWidth
                placeholder="Select Role"
                size="small"
                value={userData.isAdmin ? "Admin" : "User"}
                onChange={(e) =>
                  handleChange("isAdmin", e.target.value === "Admin")
                }
                sx={{ marginTop: "5px" }}
              >
                {roleOptions?.map((role) => (
                  <MenuItem value={role}>{role}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <FormLabel title="Link to Existing User?" />
              <TextField
                select
                fullWidth
                placeholder="Select User"
                size="small"
                value={
                  userOptions?.filter(
                    (opt) =>
                      opt.email === userData.email && opt.name === userData.name
                  )?.[0]?.email || ""
                }
                onChange={(e) =>
                  e.target.value
                    ? setUserData((prevState) => ({
                        ...prevState,
                        email: e.target.value,
                        name:
                          userOptions?.filter(
                            (opt) => opt.email === e.target.value
                          )?.[0]?.name || "",
                        isAdmin:
                          userOptions?.filter(
                            (opt) => opt.email === e.target.value
                          )?.[0]?.isAdmin || false,
                      }))
                    : null
                }
                sx={{ marginTop: "5px" }}
              >
                {optionLoading ? (
                  <MenuItem>Loading...</MenuItem>
                ) : userOptions?.length ? (
                  userOptions?.map((opt) => (
                    <MenuItem value={opt.email}>{opt.name}</MenuItem>
                  ))
                ) : (
                  <MenuItem>No Options</MenuItem>
                )}
              </TextField>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </CommonDialog>
  );
};

export default LinkProfileModal;
